/**
 * Scroll-driven "hop into the cube" for the capabilities orbit.
 *
 * While the section is simply on screen the spheres orbit the cube. As the
 * reader scrolls past it they peel off one at a time, arc up over the cube and
 * drop in through its roof; scrolling back up runs the whole thing backwards
 * and hands control back to the orbit.
 *
 * The motion is scrubbed from scroll position rather than played on a timer, so
 * reversing is free and the reader stays in control of the pace.
 *
 * Positioning moves from CSS to JS here because the hop order has to be
 * spatial: whichever sphere is nearest the roof at the moment the hop starts
 * goes first, and that cannot be expressed as a fixed stylesheet rule. When
 * this module is inactive (reduced motion, or no JS) the CSS orbit still runs
 * on its own.
 */

/** Seconds per revolution, matching the CSS orbit this takes over from. */
const ORBIT_PERIOD = 90;

/** Viewport fraction at which the cube starts hopping: dead centre. */
const HOP_ANCHOR = 0.5;

/** How far up the viewport the cube travels over the course of the hop. */
const HOP_TRAVEL = 0.45;

/** Share of the hop range each sphere occupies; the overlap is what keeps it rapid. */
const HOP_SHARE = 0.42;

/** Point within a sphere's own hop where it stops arcing and starts descending. */
const ARC_PHASE = 0.6;

/** Peak lift of the arc, in px. */
const ARC_HEIGHT = 46;

/**
 * Scale a sphere reaches once inside. Small enough that the cube, which paints
 * above the orbit, covers it completely.
 */
const SUNK_SCALE = 0.22;

/** Straight up from the cube's centre, in this coordinate space. */
const TOP_ANGLE = -Math.PI / 2;

const TAU = Math.PI * 2;

/**
 * @param {number} value
 * @returns {number}
 */
function clamp01(value) {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * @param {number} t
 * @returns {number}
 */
function easeOut(t) {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * @param {number} t
 * @returns {number}
 */
function easeIn(t) {
  return t * t * t;
}

/**
 * Shortest angle between two headings, in the range [0, PI].
 *
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */
function angleBetween(a, b) {
  let delta = (a - b) % TAU;
  if (delta < 0) delta += TAU;
  return delta > Math.PI ? TAU - delta : delta;
}

/**
 * Where one sphere sits partway through its own hop.
 *
 * Kept pure so the trajectory can be reasoned about on its own: `local` runs
 * 0 (out on the orbit) to 1 (gone, inside the cube), and nothing here depends
 * on time, so scrubbing backwards retraces the path exactly.
 *
 * @param {number} orbitX - Resting position on the orbit.
 * @param {number} orbitY
 * @param {number} roofY - Entry point just above the cube's roof.
 * @param {number} local - Progress through this sphere's hop, 0 to 1.
 * @returns {{x: number, y: number, scale: number}}
 */
function hopPosition(orbitX, orbitY, roofY, local) {
  if (local <= 0) return { x: orbitX, y: orbitY, scale: 1 };

  if (local < ARC_PHASE) {
    // Arc across to sit above the roof.
    const t = easeOut(local / ARC_PHASE);
    return {
      x: orbitX + (0 - orbitX) * t,
      y: orbitY + (roofY - orbitY) * t - ARC_HEIGHT * Math.sin(Math.PI * t),
      scale: 1,
    };
  }

  // Drop through the roof. No fade: the sphere shrinks until it is smaller
  // than the cube, which then hides it, so it genuinely goes in rather than
  // dissolving on the way.
  const t = easeIn((local - ARC_PHASE) / (1 - ARC_PHASE));
  return { x: 0, y: roofY * (1 - t), scale: 1 - (1 - SUNK_SCALE) * t };
}

/**
 * Orders spheres by how close each currently sits to the cube's roof, so the
 * hop always starts with the one that has the shortest trip.
 *
 * @param {number} orbitAngle
 * @param {number} count
 * @returns {number[]} hop position, by sphere index
 */
function rankByProximityToRoof(orbitAngle, count) {
  const order = Array.from({ length: count }, (_, index) => ({
    index,
    distance: angleBetween(orbitAngle + (TAU * index) / count, TOP_ANGLE),
    // An even count puts pairs exactly mirrored either side of twelve
    // o'clock. Both are equally "nearest", so index breaks the tie rather
    // than leaving it to whichever way floating point rounds.
  })).sort((a, b) => a.distance - b.distance || a.index - b.index);

  /** @type {number[]} */
  const rank = new Array(count);
  order.forEach((entry, position) => {
    rank[entry.index] = position;
  });

  return rank;
}

class CapabilitiesOrbit extends HTMLElement {
  /** @type {HTMLElement[]} */
  #anchors = [];

  /** @type {HTMLElement | null} */
  #ring = null;

  /** @type {HTMLElement | null} */
  #core = null;

  /**
   * Hop position of each sphere, by index. Recomputed whenever the hop restarts
   * from zero, so the order always reflects where the spheres actually are.
   *
   * @type {number[] | null}
   */
  #rank = null;

  /** @type {ResizeObserver | null} */
  #observer = null;

  #radius = 0;
  #coreSize = 0;
  #orbitAngle = 0;
  #lastTime = 0;
  #frame = 0;
  #visible = true;

  connectedCallback() {
    // Reduced motion keeps the CSS version, which already stands still.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#anchors = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.capabilities-orbit__anchor')]);
    if (!this.#anchors.length) return;

    this.#ring = this.querySelector('.capabilities-orbit__ring');
    this.#core = this.querySelector('.capabilities-orbit__core');

    // Hand over from the stylesheet: JS now owns anchor placement, so the CSS
    // spin and the per-label counter-rotation that cancelled it both go away.
    this.classList.add('capabilities-orbit--js');
    for (const anchor of this.#anchors) {
      const label = anchor.querySelector('.capabilities-orbit__label');
      if (label instanceof HTMLElement) label.style.transform = 'none';
    }

    this.#measure();
    this.#observeSize();
    this.#observeVisibility();

    this.#lastTime = performance.now();
    this.#render();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    this.#observer?.disconnect();
  }

  /**
   * Geometry comes from measured layout rather than the custom properties,
   * because those hold unresolved clamp() expressions.
   */
  #measure() {
    const label = this.querySelector('.capabilities-orbit__label');
    const labelWidth = label instanceof HTMLElement ? label.offsetWidth : 0;

    // Mirrors the CSS: the box is radius*2 wide plus one label.
    this.#radius = (this.offsetWidth - labelWidth) / 2;
    this.#coreSize = this.#core?.offsetWidth ?? 0;
  }

  #observeSize() {
    if (typeof ResizeObserver === 'undefined') return;

    this.#observer = new ResizeObserver(() => this.#measure());
    this.#observer.observe(this);
  }

  #observeVisibility() {
    if (typeof IntersectionObserver === 'undefined') return;

    const visibility = new IntersectionObserver((entries) => {
      for (const entry of entries) this.#visible = entry.isIntersecting;
    });

    visibility.observe(this);
  }

  /**
   * How far the cube has travelled up the viewport, 0 to 1.
   *
   * Measured from the cube's own position rather than from a pinned scroll
   * container: the hop runs while the reader scrolls past normally, so the
   * page is never held in place.
   *
   * @returns {number}
   */
  #scrollProgress() {
    const viewport = window.innerHeight;
    if (!viewport) return 0;

    const rect = this.getBoundingClientRect();
    const centre = rect.top + rect.height / 2;

    // Nothing happens while the cube sits at or below mid-screen; the hop
    // completes by the time it has risen close to the top.
    return clamp01((viewport * HOP_ANCHOR - centre) / (viewport * HOP_TRAVEL));
  }

  #render = () => {
    this.#frame = requestAnimationFrame(this.#render);

    const now = performance.now();
    const delta = Math.min((now - this.#lastTime) / 1000, 0.1);
    this.#lastTime = now;

    if (!this.#visible || !this.#radius) return;

    const hop = this.#scrollProgress();

    if (hop === 0) {
      // Free-running orbit, and the next hop gets a freshly measured order.
      this.#orbitAngle = (this.#orbitAngle + (delta * TAU) / ORBIT_PERIOD) % TAU;
      this.#rank = null;
    } else if (!this.#rank) {
      this.#rank = rankByProximityToRoof(this.#orbitAngle, this.#anchors.length);
    }

    const count = this.#anchors.length;
    const stagger = count > 1 ? (1 - HOP_SHARE) / (count - 1) : 0;
    const roofY = -this.#coreSize * 0.55;

    this.#anchors.forEach((anchor, index) => {
      const angle = this.#orbitAngle + (TAU * index) / count;
      const orbitX = Math.cos(angle) * this.#radius;
      const orbitY = Math.sin(angle) * this.#radius;

      const slot = this.#rank ? this.#rank[index] : index;
      const local = clamp01((hop - slot * stagger) / HOP_SHARE);
      const { x, y, scale } = hopPosition(orbitX, orbitY, roofY, local);

      anchor.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    });

    // The orbit path has nothing left to describe once they are all inside.
    if (this.#ring) this.#ring.style.opacity = `${1 - clamp01(hop * 1.4)}`;
  };
}

if (!customElements.get('capabilities-orbit')) {
  customElements.define('capabilities-orbit', CapabilitiesOrbit);
}
